import AxiosFoundationAPI from "../../../../util/AxiosFoundationAPI";
import {V1ObjectRevisionIDOCToArray} from "../../../../util/Payload";
import EnterpriseComponent from "../../EnterpriseComponent";
import {v4 as uuidv4} from "uuid";

// Foundation object revisions (t1/v1/object_revisions).
//
// ONE component serves all nine classifications. The classification filter is what makes a given
// grid a Documents grid -- set it with setClassificationFilter("Document") before the first query.
// Without it the grid would show Items and Equipment alongside Documents.
const IDOC = "v1.ObjectRevision-001";

// THE DOCUMENT EACH CLASSIFICATION READS AND WRITES THROUGH.
//
// Every first-class record is an OBJECT_REVISION row discriminated by OBJECT_CLASSIFICATION. Each of
// these documents reads through a component of its own whose constraint scopes it to exactly one
// classification, and each write through it fires that classification's own event processors --
// which is where a rule true of purchase orders and false of items will live.
//
// FALLING BACK TO THE GENERIC DOCUMENT IS DELIBERATE. Form has no component of its own, and a
// classification added to the list before its component exists must keep working rather than start
// returning nothing. An unmapped classification behaves exactly as everything did before.
const CLASSIFICATION_COMPONENTS = {
    PurchaseOrder: "PurchaseOrderRevision",
    Document: "DocumentRevision",
    Item: "ItemRevision",
    Inventory: "InventoryRevision",
    Equipment: "EquipmentRevision",
    Customer: "CustomerRevision",
    Supplier: "SupplierRevision",
    Distributor: "DistributorRevision",
    Contract: "ContractRevision",
    // Form is the tenth: not a business record but the SHAPE one is captured under, and an
    // object revision like any other. It was the classification this map deliberately fell
    // back to the generic document for; it no longer needs to.
    FORM: "FormRevision",
};
const URL = "t1/v1/object_revisions";

// The classification the open page is scoped to, held on the MODULE rather than the instance.
//
// PlatformTable does not search through the grid's EC: it constructs a SECOND, independent EC of the
// same name for the search form and runs the query on that one. Nothing the page sets on the grid's
// instance reaches it, so a search on the Documents page would return Items and Equipment too.
//
// One page is open at a time, so a module-level value is enough. Instance state still wins where it
// is set, which keeps any grid that scopes itself explicitly unaffected by whatever page came last.
let pageClassification = null;

class ObjectRevisionEnterpriseComponent extends EnterpriseComponent {
    // Which classification this grid is scoped to. Set by the page that owns the grid, NOT defaulted
    // to "Document" here -- the component is shared, and baking a default in would silently mis-scope
    // the Items or Equipment grids when they are built.
    classificationFilter = null;

    // NO IMPLICIT FILTER. This used to default to true, so a list with nothing ticked quietly asked
    // for the current revision of each object -- one row per document rather than one row per
    // revision. That hid every revision the object does not resolve to: a draft raised by Revise
    // (the predecessor stays current, by design), and for a while every cancelled first draft.
    //
    // The grid lists OBJECT REVISIONS. With no filter active it shows them all, and the Status
    // column says what each one is. Ed, after PO-00137 showed only its released revision: "There
    // are two PO-00137 object revisions. One is Draft and one is Released. If there are no filters
    // active, you must show both."
    currentOnly = false;

    // Lifecycle statuses selected in the sidebar. null or empty = no status filter.
    statusesFilter = null;

    // Recency ("Newest" / "Days old"): an ISO timestamp, keeping revisions created after it. null =
    // no filter. One field for both sidebar rows because they filter the same column -- the page
    // resolves the two windows to the single tighter cutoff before setting it.
    createdAfterFilter = null;

    // OBJECT_TYPE acronyms selected in the sidebar. null or empty = no type filter, i.e. every type.
    // Acronyms rather than USIDs because ObjectTypeAcronym is a joined attribute the document already
    // carries and the endpoint already accepts -- filtering by USID would need a column nobody reads.
    objectTypesFilter = null;

    setClassificationFilter(value) {
        this.classificationFilter = value || null;
        // Also record it for the page, so PlatformTable's separately-constructed search EC is scoped
        // the same way. See pageClassification above.
        pageClassification = this.classificationFilter;
    }

    getClassificationFilter() {
        return this.classificationFilter ?? pageClassification;
    }

    setCurrentOnly(value) {
        this.currentOnly = !!value;
    }

    /** The component this classification reads through, or null when it has none. */
    componentName() {
        return CLASSIFICATION_COMPONENTS[this.getClassificationFilter()] ?? null;
    }

    /** The document to name in -idoc and in both idoc headers. */
    idocName() {
        const component = this.componentName();
        return component ? `v1.${component}-001` : IDOC;
    }

    /** The grouping and row tags the response carries, which follow the document. */
    idocTags() {
        const component = this.componentName();
        return component ? [`ListOf${component}`, component] : ["ListOfObjectRevision", "ObjectRevision"];
    }

    /**
     * WHERE THE PAGE'S FILTERS LIVE, WHICH IS NOT ALWAYS THE INSTANCE RUNNING THE QUERY.
     *
     * PlatformTable builds a SECOND component of this class to search with -- the page never sees
     * it and never sets a filter on it -- and executes THAT one, copying its results onto the
     * grid's. So every filter the Filter By pane set sits on the grid's component while the query
     * goes out from the searching one, and a search returned the whole classification unfiltered.
     * Clearing the search ran the same path, so the list came back unfiltered too, which is where
     * Ed saw it: "after I cancel the search, I see records not filtered by the Filter By side pane."
     *
     * THE CLASSIFICATION ALREADY HAD THIS PROBLEM AND WAS ALREADY SOLVED -- with a module-level
     * `pageClassification` that the searching instance falls back to. That works and it solves it
     * for exactly one filter; the six added since were never carried, and a seventh would not be
     * either. Reading them off the companion instead fixes all of them at once and keeps working
     * as they are added, because it reads the live component rather than a copy.
     *
     * THE COMPANION IS SET BY PlatformTable (`searchEnterpriseComponent.companionEnterpriseObject`
     * = the grid's enterprise object). A component that is not somebody's search companion has
     * none, finds itself, and is unchanged.
     */
    filterSource() {
        const companion = this.companionEnterpriseObject;
        if (!companion) return this;
        const list = companion.getEnterpriseComponent(this.getName());
        return list && list !== this ? list : this;
    }

    setStatusesFilter(statuses) {
        this.statusesFilter = Array.isArray(statuses) && statuses.length > 0 ? statuses : null;
    }

    getStatusesFilter() {
        return this.statusesFilter;
    }

    setObjectTypesFilter(acronyms) {
        this.objectTypesFilter = Array.isArray(acronyms) && acronyms.length > 0 ? acronyms : null;
    }

    getObjectTypesFilter() {
        return this.objectTypesFilter;
    }

    setCreatedAfterFilter(isoTimestamp) {
        this.createdAfterFilter = isoTimestamp || null;
    }

    // THE FORM'S OWN QUESTIONS, as {code: Set<value>}. These are not columns -- the answers live
    // inside FORM_INPUT, one jsonb document per record -- so they travel as their own QBE member
    // type rather than as Attributes with invented names. See cres/idoc, FORM_FIELD_COLUMN.
    setFormFieldFilters(filters) {
        this.formFieldFilters = filters && Object.keys(filters).length > 0 ? filters : null;
    }

    getFormFieldFilters() {
        return this.formFieldFilters;
    }

    // A RANGE OVER A FORM'S OWN QUESTION, as {code: {kind, from, to}}. Separate from the value
    // filters above because it is a different question with a different answer: containment finds
    // a record, it cannot compare one, so the server reads the typed projection (FORM_VALUE)
    // rather than the jsonb document. Either bound may be blank; both blank is no filter at all.
    setFormFieldRangeFilters(ranges) {
        this.formFieldRangeFilters = ranges && Object.keys(ranges).length > 0 ? ranges : null;
    }

    getFormFieldRangeFilters() {
        return this.formFieldRangeFilters;
    }

    getCreatedAfterFilter() {
        return this.createdAfterFilter;
    }

    performQuery = (page, pageSize, foreignKey) => {
        page = page || 1;
        pageSize = pageSize || this.getDefaultCacheSize();

        this.notifyLoadStartListeners();

        let idocResponse = this.getIDOCResponse() ?? this.idocName();

        let members = [];
        let queryPayload = this.prepareQueryPayload();
        if (queryPayload.size > 0) {
            for (const [key, value] of queryPayload) {
                if (key.includes("_query")) continue;
                let operator = "EQUAL";
                if (queryPayload.get(key + "_query") !== undefined) operator = queryPayload.get(key + "_query");
                members.push({"Type": "Attribute", "Name": key, "Value": value, "Operator": operator});
            }
        }

        // Through the getter, so the search EC -- which PlatformTable builds itself and the page never
        // touches -- is scoped to the same classification as the grid it searches.
        const classification = this.getClassificationFilter();
        if (classification) {
            members.push({"Type": "Attribute", "Name": "ObjectClassification", "Value": classification, "Operator": "EQUAL"});
        }

        // EVERY FILTER BELOW COMES FROM HERE, not from `this` -- see filterSource(). On the grid's
        // own component this IS `this`; on the component PlatformTable searches with, it is the
        // grid's, which is the only place the Filter By pane ever wrote.
        const filters = this.filterSource();

        // Kept for a caller that genuinely wants one row per object -- a picker, say. It is off by
        // default, and no page turns it on. Still skipped when statuses are ticked: SUPERSEDED and
        // CANCELLED revisions are by definition not current, so both filters together match nothing.
        if (filters.currentOnly && !filters.statusesFilter) {
            members.push({"Type": "Attribute", "Name": "IsCurrent", "Value": true, "Operator": "EQUAL"});
        }

        if (filters.statusesFilter) {
            // IN takes a comma-separated STRING, not an array -- passing the array serialises as
            // "DRAFT,PUBLISHED" only by accident of coercion, and not at all once a value contains
            // anything unusual. Joined explicitly.
            members.push({"Type": "Attribute", "Name": "Status", "Value": filters.statusesFilter.join(","), "Operator": "IN"});
        }

        if (filters.objectTypesFilter) {
            // Joined the same way Status is, and for the same reason: IN takes a comma-separated
            // STRING. An acronym is A-Z and digits by construction, so nothing here needs escaping --
            // but it is joined explicitly rather than left to coerce, exactly as the statuses are.
            members.push({"Type": "Attribute", "Name": "ObjectTypeAcronym", "Value": filters.objectTypesFilter.join(","), "Operator": "IN"});
        }

        if (filters.formFieldFilters) {
            for (const [code, values] of Object.entries(filters.formFieldFilters)) {
                const wanted = Array.from(values ?? []);
                if (wanted.length === 0) continue;
                // IN takes a comma-separated STRING, as it does for an Attribute, and for the same
                // reason: an array serialises only by accident of coercion. The server ORs the
                // alternatives within one field and ANDs between fields, which is what makes
                // ticking two departments widen and ticking a department and a site narrow.
                members.push({"Type": "FormField", "Code": code, "Value": wanted.join(","),
                              "Operator": "IN"});
            }
        }

        if (filters.formFieldRangeFilters) {
            for (const [code, range] of Object.entries(filters.formFieldRangeFilters)) {
                const from = range?.from ?? "";
                const to = range?.to ?? "";
                // A RANGE NOBODY TOUCHED IS NOT A FILTER. Sending an empty pair would make the
                // grid show only the records that answer this field -- a silent narrowing the user
                // did not ask for, from a control they only looked at.
                if (from === "" && to === "") continue;
                // The kind names the CONTROL, not a column: the facet service handed it to the
                // sidebar, and the server is the only place that maps one onto a column.
                members.push({"Type": "FormFieldRange", "Code": code, "Kind": range?.kind ?? "Number",
                              "From": String(from), "To": String(to)});
            }
        }

        if (filters.createdAfterFilter) {
            // Created AFTER the cutoff, i.e. younger than the window -- "7 days old" means the last
            // seven days, not everything older than seven days.
            members.push({"Type": "Attribute", "Name": "CreatedDatetime", "Value": filters.createdAfterFilter, "Operator": "GREATER THAN"});
        }

        // THE REQUEST TAG FOLLOWS THE DOCUMENT TOO. It was the literal "ObjectRevision", which the
        // per-classification documents do not carry -- the request would match no integration
        // component and come back with nothing, an empty grid rather than an error. The same trap
        // the server-side getter had, in the same shape.
        const [, requestTag] = this.idocTags();

        let QBE = {
            "PlatformMessage": {
                "-idoc": idocResponse,
                "-id": uuidv4(),
                [requestTag]: {
                    "_attributes": {"limit": pageSize, "sort": this.getFormattedSortSpecification(), "offset": (page - 1) * pageSize},
                    "QueryGroups": {"QueryGroup": [{"Name": "primary", "Operator": "AND", "Type": "QueryGroup", "Membership": {"Member": members}}]}
                }
            }
        };

        AxiosFoundationAPI({
            method: "GET",
            url: URL,
            json: true,
            params: {"QBE": btoa(JSON.stringify(QBE))},
            // Persona/visibility, set on the EC by PlatformTable from the active visibility, so the
            // selected Mode/entity actually scopes the results.
            headers: {
                "visibility-mode": this.getVisibilityMode(),
                "visibility-value": this.getVisibilityValue(),
                "idoc-response": idocResponse,
            },
        }).then(response => {
            this.fetchEnterpriseComponentSuccess(V1ObjectRevisionIDOCToArray(response.data, ...this.idocTags()), page, pageSize);
        }).catch(error => {
            this.fetchEnterpriseComponentFailed(error);
        });
    };

    buildObjectRevisionPayload = () => {
        let o = {};
        o["USID"] = this.getAttributeValue("USID");
        o["ObjectUSID"] = this.getAttributeValue("ObjectUSID");
        o["ObjectClassification"] = this.getAttributeValue("ObjectClassification");
        o["Name"] = this.getAttributeValue("Name");
        o["Description"] = this.getAttributeValue("Description");
        o["PrimaryPositionUSID"] = this.getAttributeValue("PrimaryPositionUSID");
        o["PrimaryUserGroupUSID"] = this.getAttributeValue("PrimaryUserGroupUSID");
        o["PrimaryWorkGroupUSID"] = this.getAttributeValue("PrimaryWorkGroupUSID");
        // Create-only carriers. ObjectID and ObjectTypeUSID are joined off the parent and cannot be
        // written, so the type and (for a CUSTOMER-numbered type) the chosen number travel here.
        o["AssignObjectTypeUSID"] = this.getAttributeValue("AssignObjectTypeUSID");
        o["AssignObjectID"] = this.getAttributeValue("AssignObjectID");
        return o;
    };

    enterpriseComponentPostIDOC = () => {
        let payload = {};
        payload.PlatformMessage = {};
        payload.PlatformMessage["-idoc"] = this.idocName();
        payload.PlatformMessage[this.idocTags()[1]] = this.buildObjectRevisionPayload();
        return payload;
    };

    performInsert() {
        let payload = this.enterpriseComponentPostIDOC();
        // Same as the create dialog: the server stamps the active persona as the primary owner, and
        // only the client knows which persona is active.
        let options = {method: "POST", url: URL, json: true, data: payload, headers: {
            "idoc-request": this.idocName(),
            "idoc-response": this.idocName(),
            "visibility-mode": this.getVisibilityMode() ?? "",
            "visibility-value": this.getVisibilityValue() ?? "",
        }};
        this.notifyInsertStartListeners();
        AxiosFoundationAPI(options)
            .then(response => { this.insertEnterpriseComponentSuccess(V1ObjectRevisionIDOCToArray(response.data, ...this.idocTags())); })
            .catch(error => { this.insertEnterpriseComponentFailed(error); });
    };

    enterpriseComponentPutIDOC = () => {
        let payload = {};
        payload.PlatformMessage = {};
        payload.PlatformMessage["-idoc"] = this.idocName();
        payload.PlatformMessage[this.idocTags()[1]] = this.preparePUTPayload();
        return payload;
    };

    performUpdate() {
        let payload = this.enterpriseComponentPutIDOC();
        let options = {method: "PUT", url: URL, json: true, data: payload, headers: {"idoc-request": this.idocName(), "idoc-response": this.idocName()}};
        this.notifyUpdateStartListeners();
        AxiosFoundationAPI(options)
            .then(response => { this.updateEnterpriseComponentSuccess(V1ObjectRevisionIDOCToArray(response.data, ...this.idocTags())); })
            .catch(error => { this.updateEnterpriseComponentFailed(error); });
    };

    // Lifecycle transitions -- a PUT carrying only the target USID plus the transient LifecycleAction
    // flag. The backend pre-write handler decides whether the transition is legal (Publish is
    // DRAFT-only, Revise PUBLISHED-only, and so on) and performs the copy-forward or status change.
    // A full re-query reflects the result, since one transition can change two rows: publishing a
    // draft also supersedes the revision it replaces.
    performLifecycleAction(usid, action) {
        let payload = {};
        payload.PlatformMessage = {};
        payload.PlatformMessage["-idoc"] = this.idocName();
        payload.PlatformMessage[this.idocTags()[1]] = {"USID": usid, "LifecycleAction": action};
        let options = {method: "PUT", url: URL, json: true, data: payload, headers: {"idoc-request": this.idocName(), "idoc-response": this.idocName()}};
        this.notifyUpdateStartListeners();
        AxiosFoundationAPI(options)
            .then(() => { this.invalidate(); })
            .catch(error => { this.updateEnterpriseComponentFailed(error); });
    }

    performDelete() {
        let payload = {};
        payload.PlatformMessage = {};
        payload.PlatformMessage["-idoc"] = this.idocName();
        payload.PlatformMessage[this.idocTags()[1]] = {"USID": this.getAttributeValue("USID")};
        let options = {method: "DELETE", url: URL, json: true, data: payload, headers: {"idoc-request": this.idocName(), "idoc-response": this.idocName()}};
        this.notifyDeleteStartListeners();
        AxiosFoundationAPI(options)
            .then(() => { this.deleteEnterpriseComponentSuccess(); })
            .catch(error => { this.deleteEnterpriseComponentFailed(error); });
    };
}

export default ObjectRevisionEnterpriseComponent;
